import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, ArrowLeft } from "lucide-react";

export default function Schools() {
  const [schools, setSchools] = useState([]);
  const [form, setForm] = useState({ name: "", school_code: "", city: "" });
  const [open, setOpen] = useState(false);
  const [teacherFor, setTeacherFor] = useState(null);
  const [tForm, setTForm] = useState({ name: "", email: "", password: "" });

  const load = async () => setSchools((await api.get("/schools")).data);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/schools", form);
      toast.success(`School created — ID ${data.school_code}`);
      setForm({ name: "", school_code: "", city: "" }); setOpen(false); load();
    } catch (err) { toast.error(errText(err)); }
  };

  const toggle = async (s) => {
    try {
      await api.put(`/schools/${s.id}`, { name: s.name, city: s.city, school_code: s.school_code, active: !s.active });
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name} and all its data?`)) return;
    await api.delete(`/schools/${s.id}`); load(); toast.success("School deleted");
  };

  const saveTeacher = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/schools/${teacherFor.id}/teacher`, tForm);
      toast.success("Teacher account saved");
      setTeacherFor(null); setTForm({ name: "", email: "", password: "" }); load();
    } catch (err) { toast.error(errText(err)); }
  };

  return (
    <Shell title="Schools" subtitle="Create schools, School IDs and teacher logins" nav={
      <Link to="/admin"><Button variant="ghost" size="sm" data-testid="nav-back-admin"><ArrowLeft className="h-4 w-4 mr-1" />Dashboard</Button></Link>
    }>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-2xl sm:text-3xl font-head font-bold">All Schools ({schools.length})</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-school-btn"><Plus className="h-4 w-4 mr-1" />New School</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create School</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div><Label>School Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="school-name-input" className="mt-1.5" /></div>
              <div><Label>School ID (leave blank to auto-generate)</Label><Input value={form.school_code} onChange={(e) => setForm({ ...form, school_code: e.target.value.toUpperCase() })} data-testid="school-code-field" className="mt-1.5" /></div>
              <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="school-city-input" className="mt-1.5" /></div>
              <Button type="submit" className="w-full" data-testid="save-school-btn">Create School</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-8" data-testid="schools-list">
        {schools.length === 0 && <p className="text-slate-500">No schools yet. Create your first school.</p>}
        {schools.map((s) => (
          <div key={s.id} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`school-card-${s.school_code}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="text-lg font-semibold">{s.name}</h4>
                <p className="text-sm text-slate-500">{s.city || "—"}</p>
                <p className="mt-2 text-sm">School ID: <span className="font-mono font-semibold text-primary" data-testid={`school-code-${s.id}`}>{s.school_code}</span></p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!s.active} onCheckedChange={() => toggle(s)} data-testid={`toggle-school-${s.school_code}`} />
                <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`delete-school-${s.school_code}`}>
                  <Trash2 className="h-4 w-4 text-rose-600" />
                </Button>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600">
              <span>{s.student_count} students</span>
              <span>{s.attempt_count} attempts</span>
              <span className={s.active ? "text-emerald-600" : "text-rose-600"}>{s.active ? "Active" : "Inactive"}</span>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
              <div className="text-sm">
                <p className="text-slate-500">Teacher login</p>
                <p className="font-medium">{s.teacher ? s.teacher.email : "Not created"}</p>
              </div>
              <Button variant="outline" size="sm" data-testid={`set-teacher-${s.school_code}`}
                onClick={() => { setTeacherFor(s); setTForm({ name: s.teacher?.name || "", email: s.teacher?.email || "", password: "" }); }}>
                <KeyRound className="h-4 w-4 mr-1" />{s.teacher ? "Reset" : "Create"}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!teacherFor} onOpenChange={(v) => !v && setTeacherFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Teacher account — {teacherFor?.name}</DialogTitle></DialogHeader>
          <form onSubmit={saveTeacher} className="space-y-4">
            <div><Label>Teacher Name</Label><Input required value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} data-testid="teacher-name-input" className="mt-1.5" /></div>
            <div><Label>Email (username)</Label><Input type="email" required value={tForm.email} onChange={(e) => setTForm({ ...tForm, email: e.target.value })} data-testid="teacher-email-input" className="mt-1.5" /></div>
            <div><Label>Password</Label><Input required minLength={6} value={tForm.password} onChange={(e) => setTForm({ ...tForm, password: e.target.value })} data-testid="teacher-password-input" className="mt-1.5" /></div>
            <Button type="submit" className="w-full" data-testid="save-teacher-btn">Save Teacher Login</Button>
          </form>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
